// ─────────────────────────────────────────────
//  src/config/api.js
//
//  Price / Chart  : Yahoo Finance v8/finance/chart  (no auth)
//  Fundamentals   : NSE India official API          (no auth, needs headers)
//  Search / News  : Yahoo Finance v1/finance/search  (no auth)
//  Movers         : Nifty50 list via v8/chart sorted in-memory
// ─────────────────────────────────────────────

const CACHE_TTL = 3 * 60 * 1000;
const _cache = new Map();
function fromCache(k) {
  const e = _cache.get(k);
  if (!e || Date.now() - e.ts > CACHE_TTL) {
    _cache.delete(k);
    return null;
  }
  return e.data;
}
function toCache(k, d) {
  _cache.set(k, { data: d, ts: Date.now() });
}


// ─────────────────────────────────────────────
//  PRODUCTION UNIFIED BACKEND
// ─────────────────────────────────────────────

const PROD_API = process.env.REACT_APP_BACKEND_URL;

async function fetchUnified(symbol) {
  const sym = (symbol || "").toUpperCase();

  const url =
    `${PROD_API}/api/nse/api/quote-equity?symbol=${encodeURIComponent(sym)}`;

  const cached = fromCache(url);

  if (cached) {
    return cached;
  }

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(
      `Unified backend error ${res.status}`
    );
  }

  const data = await res.json();

  toCache(url, data);

  return data;
}

// Yahoo URL — /yf/* is proxied to query1.finance.yahoo.com
// by setupProxy.js (dev) and Netlify _redirects (prod)
function yUrl(path) {
  return `/yf${path}`;
}

// NSE URL — served by /api/nse/* Express route in setupProxy.js
// src/config/api.js

function nseUrl(path) {
  // Always use the local proxy /api/nse/* during development
  // In production, you would replace this with your actual backend URL
  return `/api/nse${path}`; 
}

// src/config/api.js

async function get(url) {
  const cached = fromCache(url);
  if (cached) return cached;

  let res = await fetch(url);
  let text = await res.text();

  // If we receive HTML instead of JSON, the session is likely blocked/expired
  if (text.trim().startsWith("<")) {
    console.warn("[API] Received HTML from NSE, likely session expiry. Retrying...");
    
    // Wait for the proxy to potentially re-init (if it has retry logic)
    await new Promise(r => setTimeout(r, 2000));
    
    res = await fetch(url);
    text = await res.text();
    
    if (text.trim().startsWith("<")) {
      throw new Error("NSE_SESSION_BLOCKED");
    }
  }

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("Non-JSON Response");
  }

  toCache(url, data);
  return data;
}

// ─────────────────────────────────────────────
//  Symbol helpers
// ─────────────────────────────────────────────
export function toYahooSymbol(symbol, exchange = "NSE") {
  if (!symbol) return "";
  const s = symbol.trim().toUpperCase();
  if (s.startsWith("^")) return s;
  if (s.endsWith(".NS") || s.endsWith(".BO")) return s;
  const OV = { "M&M": "M&M.NS", "BAJAJ-AUTO": "BAJAJ-AUTO.NS" };
  if (OV[s]) return OV[s];
  return exchange === "BSE" ? `${s}.BO` : `${s}.NS`;
}
export function fromYahooSymbol(t = "") {
  return t.replace(/\.(NS|BO)$/i, "").toUpperCase();
}

// ─────────────────────────────────────────────
//  SYMBOL SEARCH — Yahoo
// ─────────────────────────────────────────────
export async function searchSymbol(query) {
  if (!query?.trim()) return [];
  const data = await get(
    yUrl(
      `/v1/finance/search?q=${encodeURIComponent(query)}&quotesCount=10&newsCount=0`,
    ),
  );
  return (data?.quotes || []).filter(
    (q) => q.symbol?.endsWith(".NS") || q.symbol?.endsWith(".BO"),
  );
}

// ─────────────────────────────────────────────
//  Parse Yahoo chart meta → standard quote
// ─────────────────────────────────────────────
function parseMeta(meta, symbol, exchange) {
  const close = meta.regularMarketPrice || 0;
  const prev = meta.chartPreviousClose || meta.previousClose || close;
  const change = +(close - prev).toFixed(2);
  return {
    symbol,
    exchange,
    ticker: meta.symbol || toYahooSymbol(symbol, exchange),
    name: meta.longName || meta.shortName || symbol,
    price: close,
    previous_close: prev,
    change,
    change_percent: prev ? +((change / prev) * 100).toFixed(2) : 0,
    open: meta.regularMarketOpen || 0,
    high: meta.regularMarketDayHigh || 0,
    low: meta.regularMarketDayLow || 0,
    volume: meta.regularMarketVolume || 0,
    year_high: meta.fiftyTwoWeekHigh || 0,
    year_low: meta.fiftyTwoWeekLow || 0,
    currency: meta.currency || "INR",
    exchange_name: meta.exchangeName || exchange,
  };
}

// ─────────────────────────────────────────────
//  QUOTE — Yahoo v8/chart (no auth)
// ─────────────────────────────────────────────
export async function fetchQuote(
  symbol,
  exchange = "NSE"
) {
  const d = await fetchUnified(symbol);

  return {
    symbol,

    exchange,

    ticker: toYahooSymbol(
      symbol,
      exchange
    ),

    name:
      d?.info?.companyName ||
      symbol,

    price:
      d?.priceInfo?.lastPrice || 0,

    previous_close:
      d?.priceInfo?.previousClose || 0,

    change:
      d?.priceInfo?.change || 0,

    change_percent:
      d?.priceInfo?.pChange || 0,

    open:
      d?.priceInfo?.open || 0,

    high:
      d?.fundamentals?.fiftyTwoWeekHigh || 0,

    low:
      d?.fundamentals?.fiftyTwoWeekLow || 0,

    volume: 0,

    year_high:
      d?.fundamentals?.fiftyTwoWeekHigh || 0,

    year_low:
      d?.fundamentals?.fiftyTwoWeekLow || 0,

    currency:
      d?.info?.currency || "INR",

    exchange_name:
      d?.info?.exchange || exchange,
  };
}

// ─────────────────────────────────────────────
//  NSE INDIA — sector, industry, ISIN, face value
//  Route: /api/nse/api/quote-equity?symbol=WIPRO
//  Handled by setupProxy.js as plain Express route
//  using Node https with NSE session headers.
// ─────────────────────────────────────────────
async function fetchNseData(symbol) {
  const sym = fromYahooSymbol(toYahooSymbol(symbol, "NSE"));
  const url = nseUrl(`/api/quote-equity?symbol=${encodeURIComponent(sym)}`);
  const cached = fromCache(url);
  if (cached) return cached;

  // Inside fetchNseData in api.js
  try {
    const res = await fetch(url);
    const text = await res.text();

    if (text.trim().startsWith("<")) {
      // If we get HTML, wait 1 second and try ONE more time
      // This gives the proxy time to re-init the session
      console.warn(`[NSE] ${sym} returned HTML, retrying...`);
      await new Promise((r) => setTimeout(r, 1500));
      const retryRes = await fetch(url);
      const retryText = await retryRes.text();
      if (retryText.trim().startsWith("<")) return null;
      return JSON.parse(retryText);
    }
    return JSON.parse(text);
  } catch (e) {
    console.warn(`[NSE] error for ${sym}:`, e.message);
    return null;
  }
}

// ─────────────────────────────────────────────
//  OVERVIEW — Yahoo chart meta + NSE sector info
// ─────────────────────────────────────────────

export async function fetchOverview(
  symbol,
  exchange = "NSE"
) {
  const d = await fetchUnified(symbol);

  return {
    symbol,
    exchange,

    price:
      d?.priceInfo?.lastPrice || 0,

    previous_close:
      d?.priceInfo?.previousClose || 0,

    change:
      d?.priceInfo?.change || 0,

    change_percent:
      d?.priceInfo?.pChange || 0,

    open:
      d?.priceInfo?.open || 0,

    company_name:
      d?.info?.companyName ||
      symbol,

    about: "",

    sector:
      d?.info?.sector || "",

    industry:
      d?.info?.industry || "",

    company_market_cap:
      d?.fundamentals?.marketCap || 0,

    company_pe_ratio:
      d?.fundamentals?.peRatio || 0,

    company_pb_ratio:
      d?.fundamentals?.pbRatio || 0,

    company_eps:
      d?.fundamentals?.eps || 0,

    company_dividend_yield:
      d?.fundamentals?.dividendYield || 0,

    company_roe:
      d?.fundamentals?.roe || 0,

    company_roa:
      d?.fundamentals?.roa || 0,

    book_value:
      d?.fundamentals?.bookValue || 0,

    debt_to_equity:
      d?.fundamentals?.debtToEquity || 0,

    profit_margin:
      d?.fundamentals?.profitMargin || 0,

    company_employees:
      d?.info?.employees || 0,

    isin: "",

    face_value: 0,

    issued_size: 0,

    listing_date: "",

    trading_status: "Active",

    year_high:
      d?.fundamentals?.fiftyTwoWeekHigh || 0,

    year_low:
      d?.fundamentals?.fiftyTwoWeekLow || 0,

    last_update_utc:
      d?.metadata?.lastUpdateTime ||
      new Date().toISOString(),

    promoter_holding:
      d?.shareholding?.promoter ||
      "—",

    institutional_holding:
      d?.shareholding?.institutions ||
      "—",
  };
}

// ─────────────────────────────────────────────
//  TIME SERIES — Yahoo v8/chart
// ─────────────────────────────────────────────
const PERIOD_MAP = {
  "1W": { range: "5d", interval: "15m" },
  "1M": { range: "1mo", interval: "1d" },
  "3M": { range: "3mo", interval: "1d" },
  "6M": { range: "6mo", interval: "1wk" },
  "1Y": { range: "1y", interval: "1wk" },
};
export async function fetchTimeSeries(symbol, exchange = "NSE", period = "1M") {
  const ticker = toYahooSymbol(symbol, exchange);
  const { range, interval } = PERIOD_MAP[period] || PERIOD_MAP["1M"];
  const data = await get(
    yUrl(
      `/v8/finance/chart/${encodeURIComponent(ticker)}?range=${range}&interval=${interval}&includePrePost=false`,
    ),
  );
  const result = data?.chart?.result?.[0];
  if (!result) throw new Error(`No chart data for "${ticker}"`);
  const ts = result.timestamp || [];
  const q = result.indicators?.quote?.[0] || {};
  return {
    time_series: ts
      .map((t, i) => ({
        date: new Date(t * 1000).toISOString().slice(0, 10),
        open: q.open?.[i] ?? 0,
        high: q.high?.[i] ?? 0,
        low: q.low?.[i] ?? 0,
        close: q.close?.[i] ?? 0,
        volume: q.volume?.[i] ?? 0,
      }))
      .filter((c) => c.close > 0),
  };
}

export async function fetchNews(
  symbol
) {
  try {
    const res = await fetch(
      `${PROD_API}/api/stock/${encodeURIComponent(symbol)}/news`
    );

    if (!res.ok) {
      return { news: [] };
    }

    const data = await res.json();

    return {
      news: Array.isArray(data)
        ? data
        : data?.news || [],
    };
  } catch {
    return {
      news: [],
    };
  }
}

// ─────────────────────────────────────────────
//  FINANCIALS — Yahoo Finance v10/quoteSummary
//
//  Uses modules that DON'T need crumb auth:
//  • incomeStatementHistory / incomeStatementHistoryQuarterly
//  • balanceSheetHistory    / balanceSheetHistoryQuarterly
//  • cashflowStatementHistory / cashflowStatementHistoryQuarterly
//
//  Values come as { raw: number, fmt: "string" }
//  All go through the existing /yf/* proxy — no new routes needed.
// ─────────────────────────────────────────────

const _yfFinCache = {};

async function fetchYfFinancials(symbol, exchange, period) {
  const ticker = toYahooSymbol(symbol, exchange);
  const isQ    = period === "QUARTERLY";

  const modules = isQ
    ? "incomeStatementHistoryQuarterly,balanceSheetHistoryQuarterly,cashflowStatementHistoryQuarterly"
    : "incomeStatementHistory,balanceSheetHistory,cashflowStatementHistory";

  const key = `${ticker}__${period}`;
  if (_yfFinCache[key] && Date.now() - _yfFinCache[key].ts < CACHE_TTL) {
    return _yfFinCache[key].data;
  }

  try {
    const url  = yUrl(`/v10/finance/quoteSummary/${encodeURIComponent(ticker)}?modules=${encodeURIComponent(modules)}`);
    const res  = await get(url);
    const data = res?.quoteSummary?.result?.[0] ?? null;
    if (!data) throw new Error("No quoteSummary result");
    _yfFinCache[key] = { data, ts: Date.now() };
    return data;
  } catch (e) {
    console.warn("[fetchYfFinancials]", e.message);
    return null;
  }
}

// raw() — Yahoo wraps numbers as { raw: 123, fmt: "123" }; handle both
const rawN = (v) => {
  if (v == null) return null;
  const n = typeof v === "object" ? v?.raw : v;
  return n != null && !isNaN(Number(n)) ? Number(n) : null;
};

// Format a Unix timestamp → "Q2 FY25" style label
function fmtPeriod(endDateObj) {
  const ts = rawN(endDateObj);
  if (!ts) return "";
  const d  = new Date(ts * 1000);
  const yr = d.getFullYear();
  const mo = d.getMonth() + 1; // 1–12
  const fy = mo <= 3 ? yr : yr + 1;          // Indian FY ends March
  const q  = mo <= 3 ? 4 : mo <= 6 ? 1 : mo <= 9 ? 2 : 3;
  return `Q${q} FY${String(fy).slice(2)}`;
}

export async function fetchIncomeStatement(
  symbol,
  exchange,
  period = "QUARTERLY"
) {
  const d = await fetchUnified(symbol);

  const rows =
    period === "QUARTERLY"
      ? d?.financials?.pnlQuarterly
      : d?.financials?.pnlAnnual;

  return {
    income_statement:
      (rows || []).map((r) => ({
        period:
          r?.year ||
          r?.date ||
          "—",

        total_revenue:
          r?.totalRevenue || 0,

        ebitda:
          r?.operatingIncome || 0,

        net_income:
          r?.netIncome || 0,
      })),
  };
}

export async function fetchBalanceSheet(
  symbol
) {
  const d = await fetchUnified(symbol);

  return {
    balance_sheet: [
      {
        period: "Latest",

        total_assets:
          d?.financials?.totalAssets || 0,

        total_liabilities:
          d?.financials?.totalLiabilities || 0,

        total_equity:
          Math.max(
            0,
            (d?.financials?.totalAssets || 0) -
            (d?.financials?.totalLiabilities || 0)
          ),
      },
    ],
  };
}

export async function fetchCashFlow(
  symbol
) {
  const d = await fetchUnified(symbol);

  return {
    cash_flow: [
      {
        period: "Latest",

        operating_cash_flow:
          d?.financials?.operatingIncome || 0,

        investing_cash_flow:
          d?.financials?.totalCash || 0,

        financing_cash_flow:
          d?.financials?.totalDebt || 0,
      },
    ],
  };
}

// ─────────────────────────────────────────────
//  INDICES
// ─────────────────────────────────────────────
const INDEX_CONFIG = [
  { label: "NIFTY 50", ticker: "^NSEI" },
  { label: "SENSEX", ticker: "^BSESN" },
  { label: "NIFTY BANK", ticker: "^NSEBANK" },
  { label: "NIFTY IT", ticker: "^CNXIT" },
];
async function fetchIndexTicker(ticker) {
  try {
    const data = await get(
      yUrl(
        `/v8/finance/chart/${encodeURIComponent(ticker)}?range=1d&interval=1d&includePrePost=false`,
      ),
    );
    const result = data?.chart?.result?.[0];
    if (!result) return null;

    const meta = result.meta;
    const close = meta.regularMarketPrice || 0;
    const prev = meta.chartPreviousClose || meta.previousClose || close;
    const change = +(close - prev).toFixed(2);

    return {
      symbol: ticker,
      ticker: ticker,
      name: meta.longName || meta.shortName || ticker,
      price: close,
      previous_close: prev,
      change,
      change_percent: prev ? +((change / prev) * 100).toFixed(2) : 0,
      open: meta.regularMarketOpen || 0,
      high: meta.regularMarketDayHigh || 0,
      low: meta.regularMarketDayLow || 0,
      volume: meta.regularMarketVolume || 0,
      year_high: meta.fiftyTwoWeekHigh || 0,
      year_low: meta.fiftyTwoWeekLow || 0,
    };
  } catch (e) {
    console.warn(`[Index] Error fetching ${ticker}:`, e.message);
    return null;
  }
}

let _indicesCache = null,
  _indicesCacheTs = 0;

export async function fetchIndices() {
  if (_indicesCache && Date.now() - _indicesCacheTs < CACHE_TTL) {
    return _indicesCache;
  }

  const results = await Promise.allSettled(
    INDEX_CONFIG.map(({ ticker }) => fetchIndexTicker(ticker)),
  );

  const indices = INDEX_CONFIG.map(({ label }, i) => ({
    label,
    ...(results[i].status === "fulfilled" && results[i].value
      ? results[i].value
      : { error: true }),
  }));

  _indicesCache = indices;
  _indicesCacheTs = Date.now();

  return indices;
}

// ─────────────────────────────────────────────
//  MARKET MOVERS — Nifty50 via Yahoo, sorted in-memory
// ─────────────────────────────────────────────
const NIFTY50_YF = [
  "RELIANCE.NS",
  "TCS.NS",
  "HDFCBANK.NS",
  "INFY.NS",
  "ICICIBANK.NS",
  "HINDUNILVR.NS",
  "SBIN.NS",
  "BHARTIARTL.NS",
  "ITC.NS",
  "KOTAKBANK.NS",
  "LT.NS",
  "AXISBANK.NS",
  "BAJFINANCE.NS",
  "ASIANPAINT.NS",
  "MARUTI.NS",
  "HCLTECH.NS",
  "SUNPHARMA.NS",
  "TITAN.NS",
  "ULTRACEMCO.NS",
  "WIPRO.NS",
  "ONGC.NS",
  "POWERGRID.NS",
  "NTPC.NS",
  "TMCV.NS",
  "TATASTEEL.NS",
  "ADANIPORTS.NS",
  "COALINDIA.NS",
  "BAJAJFINSV.NS",
  "TECHM.NS",
  "NESTLEIND.NS",
  "DIVISLAB.NS",
  "DRREDDY.NS",
  "CIPLA.NS",
  "INDUSINDBK.NS",
  "APOLLOHOSP.NS",
  "JSWSTEEL.NS",
  "BPCL.NS",
  "EICHERMOT.NS",
  "HEROMOTOCO.NS",
  "BRITANNIA.NS",
  "GRASIM.NS",
  "ADANIENT.NS",
  "TATACONSUM.NS",
  "HINDALCO.NS",
  "SBILIFE.NS",
  "HDFCLIFE.NS",
  "UPL.NS",
  "VEDL.NS",
  "M&M.NS",
  "BAJAJ-AUTO.NS",
];

async function fetchOneTicker(yfTicker) {
  try {
    const data = await get(
      yUrl(
        `/v8/finance/chart/${encodeURIComponent(yfTicker)}?range=1d&interval=1d&includePrePost=false`,
      ),
    );
    const result = data?.chart?.result?.[0];
    if (!result) return null;
    const meta = result.meta;
    const close = meta.regularMarketPrice || 0;
    const prev = meta.chartPreviousClose || meta.previousClose || close;
    const change = +(close - prev).toFixed(2);
    return {
      name: meta.longName || meta.shortName || yfTicker,
      symbol: yfTicker,
      ticker: yfTicker,
      price: close,
      change,
      change_percent: prev ? +((change / prev) * 100).toFixed(2) : 0,
      volume: meta.regularMarketVolume || 0,
    };
  } catch {
    return null;
  }
}

let _niftyCache = null,
  _niftyCacheTs = 0;
async function getNifty50() {
  if (_niftyCache && Date.now() - _niftyCacheTs < CACHE_TTL) return _niftyCache;
  const BATCH = 8,
    all = [];
  for (let i = 0; i < NIFTY50_YF.length; i += BATCH) {
    const results = await Promise.all(
      NIFTY50_YF.slice(i, i + BATCH).map(fetchOneTicker),
    );
    results.forEach((r) => r && all.push(r));
    if (i + BATCH < NIFTY50_YF.length)
      await new Promise((r) => setTimeout(r, 200));
  }
  _niftyCache = all;
  _niftyCacheTs = Date.now();
  return all;
}

export async function fetchGainers() {
  const a = await getNifty50();
  return {
    trends: [...a]
      .sort((x, y) => y.change_percent - x.change_percent)
      .slice(0, 10),
  };
}
export async function fetchLosers() {
  const a = await getNifty50();
  return {
    trends: [...a]
      .sort((x, y) => x.change_percent - y.change_percent)
      .slice(0, 10),
  };
}
export async function fetchMostActive() {
  const a = await getNifty50();
  return { trends: [...a].sort((x, y) => y.volume - x.volume).slice(0, 10) };
}

// ─────────────────────────────────────────────
//  BATCH QUOTES
// ─────────────────────────────────────────────
export async function fetchBatchQuotes(holdings) {
  const results = await Promise.allSettled(
    holdings.map(({ symbol, exchange }) => fetchQuote(symbol, exchange)),
  );
  return holdings.map(({ symbol }, i) => ({
    symbol,
    ...(results[i].status === "fulfilled" ? results[i].value : null),
    error: results[i].status === "rejected",
  }));
}